import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import api from '@/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingContact from '@/components/FloatingContact';
import SEOHead from '@/components/SEOHead';
import { trackFormSubmit, trackCatalogDownload, trackWhatsAppClick, trackPhoneClick, trackEmailClick } from '@/utils/analytics';
import { MessageCircle, Phone, Mail, ArrowRight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';

const RevealSection = ({ children, className = '' }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  return (
    <div ref={ref} className={`bl-reveal ${inView ? 'visible' : ''} ${className}`}>
      {children}
    </div>
  );
};

const HomePage = () => {
  const { lang, getLocalized, getLocalizedPath } = useLanguage();
  const [hero, setHero] = useState(null);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', type: 'Homeowner', message: '', website: '' });
  const [formStatus, setFormStatus] = useState('');
  const [catalogEmail, setCatalogEmail] = useState('');
  const [catalogStatus, setCatalogStatus] = useState('');
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heroRes, productsRes, projectsRes, catalogRes, settingsRes] = await Promise.all([
          api.get('/hero'),
          api.get('/products'),
          api.get('/projects'),
          api.get('/catalog'),
          api.get('/site-settings')
        ]);
        setHero(heroRes.data);
        setProducts(productsRes.data);
        setProjects(projectsRes.data);
        setCatalog(catalogRes.data);
        setSettings(settingsRes.data);
        if (settingsRes.data?.maintenance_mode) setMaintenance(true);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    fetchData();
  }, []);

  const heroData = hero ? getLocalized(hero) : {};
  const catalogData = catalog ? getLocalized(catalog) : {};
  const contact = settings?.contact || {};

  // Clean any "aluminum/aluminio" from API content
  const cleanText = (text) => {
    if (!text) return text;
    return text
      .replace(/aluminum\s*/gi, '')
      .replace(/aluminio\s*/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };
  const whyData = translations.why[lang];
  const trustData = translations.trustBar[lang];
  const prodTexts = translations.products[lang];
  const ctaTexts = translations.inlineCta[lang];
  const galleryTexts = translations.gallery[lang];
  const contactTexts = translations.contact[lang];
  const locTexts = translations.locations[lang];

  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const getCatalogUrl = () => {
    const pdfUrl = catalog?.pdf_url;
    if (pdfUrl) {
      if (pdfUrl.startsWith('http')) return pdfUrl;
      return `${process.env.REACT_APP_BACKEND_URL}${pdfUrl}`;
    }
    return '/Blackline_PS_Product_Catalog_2026.pdf';
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      await api.post('/leads', { ...formData, locale: lang, page: 'contact' });
      trackFormSubmit('contact', { type: formData.type, page: 'homepage' });
      setFormStatus('success');
      setFormData({ first_name: '', last_name: '', email: '', phone: '', type: 'Homeowner', message: '', website: '' });
    } catch (err) {
      setFormStatus('error');
    }
  };

  const handleCatalogSubmit = async (e) => {
    e.preventDefault();
    setCatalogStatus('sending');
    try {
      await api.post('/catalog-request', { email: catalogEmail });
      trackCatalogDownload('email');
      setCatalogStatus('success');
      setCatalogEmail('');
    } catch (err) {
      setCatalogStatus('error');
    }
  };

  if (maintenance) {
    const mt = translations.maintenance[lang];
    return (
      <div className="bl-maintenance">
        <div>
          <h1>{mt.title}</h1>
          <p>{mt.desc}</p>
          <p>{mt.contact} <a href={`mailto:${contact.email}`}>{contact.email}</a></p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="homepage">
      <SEOHead
        title={lang === 'es' ? 'Ventanas y Puertas Premium' : 'Premium Windows & Doors'}
        description={lang === 'es'
          ? 'Sistemas premium de ventanas y puertas para arquitectura moderna. Sirviendo todo Texas.'
          : 'Premium window and door systems for modern architecture. Serving all of Texas.'}
        path="/"
      />
      <Navbar />

      {/* 1. HERO */}
      <section className="bl-hero" data-testid="hero-section">
        <div className="bl-hero-bg" style={{ backgroundImage: `url(${hero?.image_url || ''})` }} />
        <div className="bl-hero-frame" />
        <div className="bl-hero-content">
          <div className="bl-hero-eyebrow">{cleanText(heroData.eyebrow) || 'Windows & Doors'}</div>
          <h1 className="bl-hero-title" dangerouslySetInnerHTML={{
            __html: lang === 'es'
              ? 'Sistemas Modernos<br/>para Arquitectura<br/><em>Contempor\u00e1nea.</em>'
              : 'Modern Systems<br/>for <em>Contemporary</em><br/>Architecture.'
          }} />
          <p className="bl-hero-sub">{cleanText(heroData.subhead) || ''}</p>
          <div className="bl-hero-actions">
            <a href="#contact" className="btn-gold" onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="hero-cta-quote">
              {heroData.cta1_text || 'Request a Quote'}
            </a>
            <a href="#products" className="btn-ghost" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="hero-cta-products">
              {heroData.cta2_text || 'Explore Products'}
            </a>
          </div>
        </div>
        <div className="bl-hero-scroll">
          <span>{lang === 'es' ? 'Desliza' : 'Scroll'}</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <div className="bl-trust-bar" data-testid="trust-bar">
        {trustData.map((item, i) => (
          <div className="bl-trust-item" key={i}>
            <div className="num">{item.num}</div>
            <div className="label">{item.label}</div>
          </div>
        ))}
      </div>

      {/* 3. PRODUCTS */}
      <section className="bl-section bl-products" id="products" data-testid="products-section">
        <RevealSection>
          <div className="bl-section-eyebrow">{prodTexts.eyebrow}</div>
          <div className="bl-section-title">{prodTexts.title}</div>
          <div className="bl-section-desc">{prodTexts.desc}</div>
        </RevealSection>
        <div className="bl-products-grid">
          {products.map((product) => {
            const loc = getLocalized(product);
            return (
              <Link
                to={getLocalizedPath(`/products/${product.slug}`)}
                className="bl-product-card"
                key={product.id}
                data-testid={`product-card-${product.slug}`}
              >
                <img
                  src={product.image_url}
                  alt={loc.name}
                  className="bl-product-card-img"
                  loading="lazy"
                />
                <div className="bl-product-card-body">
                  <div className="bl-product-card-name">{loc.name}</div>
                  <div className="bl-product-card-sub">{cleanText(loc.one_liner)}</div>
                  <div className="bl-product-card-link">{lang === 'es' ? 'Explorar' : 'Explore'} <ArrowRight size={12} /></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 4. WHY BLACKLINE */}
      <section className="bl-why" id="why" data-testid="why-section">
        <RevealSection>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div className="bl-section-eyebrow">{whyData.eyebrow}</div>
              <div className="bl-section-title" style={{ color: 'var(--white)' }}>{whyData.title}</div>
            </div>
            <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: '2.5rem', color: 'var(--gold)', lineHeight: 1 }}>Since 2015</div>
              <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: '0.3rem' }}>
                {lang === 'es' ? '+11 A\u00f1os de Excelencia' : '11+ Years of Excellence'}
              </div>
            </div>
          </div>
        </RevealSection>
        <div className="bl-why-grid">
          {whyData.items.map((item, i) => (
            <div className="bl-why-item" key={i}>
              <div className="why-num">{item.num}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. INLINE CTA */}
      <section className="bl-inline-cta" data-testid="inline-cta">
        <div className="bl-inline-cta-left">
          <h2>{ctaTexts.title}</h2>
          <p>{ctaTexts.desc}</p>
          <div className="bl-inline-cta-actions">
            <a href="sms:+13466156860" className="bl-cta-channel" data-testid="cta-sms">
              <MessageCircle size={18} className="bl-cta-channel-icon" /> SMS / Text
            </a>
            <a href="tel:+13466156860" className="bl-cta-channel" data-testid="cta-call" onClick={() => trackPhoneClick('+1 (346) 615-6860', 'inline-cta')}>
              <Phone size={18} className="bl-cta-channel-icon" /> {lang === 'es' ? 'Llamar' : 'Call Us'}
            </a>
            <a href="mailto:sales@blacklineps.com" className="bl-cta-channel" data-testid="cta-email" onClick={() => trackEmailClick('inline-cta')}>
              <Mail size={18} className="bl-cta-channel-icon" /> Email
            </a>
          </div>
        </div>
        <div
          className="bl-inline-cta-right"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1600607688969-a5bfcd646154?w=800&q=80)` }}
        />
      </section>

      {/* 6. GALLERY */}
      <section className="bl-gallery" id="gallery" data-testid="gallery-section">
        <div className="bl-gallery-header">
          <RevealSection>
            <div className="bl-section-eyebrow">{galleryTexts.eyebrow}</div>
            <div className="bl-section-title">{galleryTexts.title}</div>
          </RevealSection>
        </div>
        <div className="bl-gallery-scroll">
          {projects.map((project) => {
            const loc = getLocalized(project);
            return (
              <div className="bl-gallery-item" key={project.id}>
                <img src={project.image_url} alt={loc.title} loading="lazy" />
                <div className="bl-gallery-item-overlay">
                  <h4>{loc.title}</h4>
                  <span>{loc.location}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 7. CATALOG */}
      <section className="bl-catalog" id="catalog" data-testid="catalog-section">
        <div className="bl-catalog-visual">
          <div className="bl-catalog-mock-shadow" />
          <div className="bl-catalog-mock" />
        </div>
        <div className="bl-catalog-content">
          <div className="bl-section-eyebrow">{catalogData.eyebrow || 'Resources'}</div>
          <h2>{catalogData.title || 'Download Our Product Catalog.'}</h2>
          <p>{catalogData.description || ''}</p>
          {catalogStatus === 'success' ? (
            <div>
              <div className="bl-form-success" style={{ marginBottom: '1.5rem' }}>
                {lang === 'es' ? '¡Gracias! Tu enlace de descarga está listo.' : 'Thank you! Your download is ready.'}
              </div>
              <a href={getCatalogUrl()} target="_blank" rel="noopener noreferrer" className="bl-catalog-submit" style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }} onClick={() => trackCatalogDownload('email')}>
                {lang === 'es' ? 'Descargar Catálogo' : 'Download Catalog'}
              </a>
            </div>
          ) : (
            <form className="bl-catalog-form" onSubmit={handleCatalogSubmit}>
              <input
                type="email"
                className="bl-catalog-input"
                placeholder={catalogData.input_placeholder || 'Your email address'}
                value={catalogEmail}
                onChange={(e) => setCatalogEmail(e.target.value)}
                required
                data-testid="catalog-email"
              />
              <button type="submit" className="bl-catalog-submit" disabled={catalogStatus === 'sending'} data-testid="catalog-submit">
                {catalogStatus === 'sending' ? '...' : (catalogData.button_text || 'Download')}
              </button>
            </form>
          )}
          <div className="bl-catalog-alt">
            <a href={getCatalogUrl()} target="_blank" rel="noopener noreferrer" onClick={() => trackCatalogDownload('direct')}>
              {lang === 'es' ? 'O descarga directamente sin email.' : 'Or download directly without email.'}
            </a>
          </div>
        </div>
      </section>

      {/* 8. CONTACT */}
      <section className="bl-contact" id="contact" data-testid="contact-section">
        <div className="bl-contact-intro">
          <RevealSection>
            <div className="bl-section-eyebrow">{contactTexts.eyebrow}</div>
            <div className="bl-section-title">{contactTexts.title}</div>
            <div className="bl-section-desc">{contactTexts.desc}</div>
          </RevealSection>

          <div className="bl-contact-primary-channels">
            <a href="sms:+13466156860" className="bl-primary-channel" data-testid="contact-sms">
              <div className="bl-channel-icon"><MessageCircle size={20} /></div>
              <div className="bl-channel-text">
                <h4>{contactTexts.whatsapp}</h4>
                <p>(346) 615-6860</p>
              </div>
              <div className="bl-channel-arrow"><ArrowRight size={18} /></div>
            </a>
            <a href="mailto:sales@blacklineps.com" className="bl-primary-channel" data-testid="contact-email-channel">
              <div className="bl-channel-icon"><Mail size={20} /></div>
              <div className="bl-channel-text">
                <h4>{contactTexts.email}</h4>
                <p>sales@blacklineps.com</p>
              </div>
              <div className="bl-channel-arrow"><ArrowRight size={18} /></div>
            </a>
            <a href="tel:+13466156860" className="bl-primary-channel" data-testid="contact-call-houston">
              <div className="bl-channel-icon"><Phone size={20} /></div>
              <div className="bl-channel-text">
                <h4>{contactTexts.callHouston}</h4>
                <p>(346) 615-6860</p>
              </div>
              <div className="bl-channel-arrow"><ArrowRight size={18} /></div>
            </a>
          </div>

          <div className="bl-contact-secondary">
            <a href="https://wa.me/13466156860" className="bl-secondary-link" target="_blank" rel="noopener noreferrer">
              <MessageCircle size={14} /> WhatsApp: (346) 615-6860
            </a>
          </div>
        </div>

        <div className="bl-contact-form">
          {formStatus === 'success' ? (
            <div className="bl-form-success" data-testid="form-success">
              {contactTexts.successMsg}
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} data-testid="contact-form">
              <div className="bl-form-row">
                <div className="bl-form-field">
                  <label>{contactTexts.firstName}</label>
                  <input type="text" name="first_name" placeholder="John" value={formData.first_name} onChange={handleFormChange} required data-testid="form-firstname" />
                </div>
                <div className="bl-form-field">
                  <label>{contactTexts.lastName}</label>
                  <input type="text" name="last_name" placeholder="Smith" value={formData.last_name} onChange={handleFormChange} required data-testid="form-lastname" />
                </div>
              </div>
              <div className="bl-form-field">
                <label>{contactTexts.emailLabel}</label>
                <input type="email" name="email" placeholder="john@example.com" value={formData.email} onChange={handleFormChange} required data-testid="form-email" />
              </div>
              <div className="bl-form-field">
                <label>{contactTexts.phone}</label>
                <input type="tel" name="phone" placeholder="(555) 000-0000" value={formData.phone} onChange={handleFormChange} data-testid="form-phone" />
              </div>
              <div className="bl-form-field">
                <label>{contactTexts.iAmA}</label>
                <select name="type" value={formData.type} onChange={handleFormChange} data-testid="form-type">
                  {contactTexts.types.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="bl-form-field">
                <label>{contactTexts.projectDetails}</label>
                <textarea name="message" placeholder={lang === 'es' ? 'Ubicaci\u00f3n, tipo de proyecto, productos de inter\u00e9s, timeline...' : 'Location, type of project, products of interest, timeline...'} value={formData.message} onChange={handleFormChange} data-testid="form-message" />
              </div>
              {/* Honeypot - hidden from real users */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <input type="text" name="website" value={formData.website} onChange={handleFormChange} tabIndex={-1} autoComplete="off" />
              </div>
              <button type="submit" className="bl-form-submit" disabled={formStatus === 'sending'} data-testid="form-submit">
                {formStatus === 'sending' ? (lang === 'es' ? 'Enviando...' : 'Sending...') : contactTexts.submit}
              </button>
              {formStatus === 'error' && (
                <p style={{ color: '#ef4444', marginTop: '1rem', fontSize: '0.88rem' }}>
                  {lang === 'es' ? 'Error al enviar. Int\u00e9ntalo de nuevo.' : 'Error sending. Please try again.'}
                </p>
              )}
            </form>
          )}
        </div>
      </section>

      {/* 9. LOCATIONS */}
      <section className="bl-locations" data-testid="locations-section">
        <RevealSection>
          <div className="bl-section-eyebrow">{lang === 'es' ? 'Cobertura' : 'Coverage'}</div>
          <div className="bl-section-title">{lang === 'es' ? 'Sirviendo a Todo Texas.' : 'Serving All of Texas.'}</div>
        </RevealSection>
        <div className="bl-locations-grid">
          <div className="bl-location-card">
            <div className="bl-location-city">{lang === 'es' ? 'Oficina Principal' : 'Main Office'}</div>
            <div className="bl-location-info">
              Blackline Premium Solutions<br />
              1250 W Sam Houston Pkwy<br />
              Houston, TX 77042<br />
              (346) 615-6860<br />
              sales@blacklineps.com<br />
              {contact.hours || 'Monday \u2013 Friday, 8:30 AM \u2013 5:30 PM'}
            </div>
            <a href="https://maps.google.com/?q=1250+W+Sam+Houston+Pkwy+Houston+TX+77042" target="_blank" rel="noopener noreferrer" className="bl-location-link">{lang === 'es' ? 'C\u00f3mo Llegar \u2192' : 'Get Directions \u2192'}</a>
          </div>
          <div className="bl-location-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="bl-location-city">{lang === 'es' ? 'Cobertura Estatal' : 'Statewide Delivery'}</div>
            <div className="bl-location-info" style={{ fontSize: '0.95rem', lineHeight: '1.9' }}>
              {lang === 'es'
                ? 'Entregamos e instalamos en todo el estado de Texas. Houston, Dallas, San Antonio, Austin, Fort Worth, El Paso, Lubbock y m\u00e1s. Cont\u00e1ctanos para una cotizaci\u00f3n en tu ciudad.'
                : 'We deliver and install across the entire state of Texas. Houston, Dallas, San Antonio, Austin, Fort Worth, El Paso, Lubbock, and beyond. Contact us for a quote in your city.'}
            </div>
          </div>
        </div>
      </section>

      {/* 10. FOOTER */}
      <Footer products={products} settings={settings} />

      {/* 11. FLOATING CONTACT */}
      <FloatingContact settings={settings} />
    </div>
  );
};

export default HomePage;
